import { useState } from 'react';
import { Link } from 'react-router-dom';
import AppHeader from '../components/AppHeader';
import Lightbox from '../components/Lightbox';
import { useAuth } from '../lib/auth';
import { filterIdeas, useDateFormatter, useIdeas } from '../lib/useIdeas';

export default function Notes() {
  const { user } = useAuth();
  const { ideas, error, loading } = useIdeas(user?.uid);
  const [search, setSearch] = useState('');
  const [viewing, setViewing] = useState<{ images: string[]; start: number } | null>(null);
  const fmt = useDateFormatter();

  const shown = filterIdeas(ideas, search);

  return (
    <>
      <AppHeader>
        <input
          className="search"
          type="search"
          placeholder="Search your ideas"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Link className="button primary" to="/new">New text note</Link>
        {/* Present but inert, so the web offers the same two ways in as the phones and says
            plainly which one it lacks. aria-disabled rather than disabled: a disabled button
            takes no focus and, in some browsers, no hover — the explanation would be
            unreachable for exactly the people trying to use it. */}
        <span className="has-tooltip">
          <button
            type="button"
            aria-disabled="true"
            aria-describedby="audio-note-tooltip"
          >
            New audio note
          </button>
          <span role="tooltip" id="audio-note-tooltip" className="tooltip">
            Audio note creation is not yet available on desktop. Record one in the iOS or
            Android app.
          </span>
        </span>
      </AppHeader>

      <main>
        {loading && <p className="muted">Loading…</p>}
        {error && <p className="error">{error}</p>}

        {!loading && !error && shown.length === 0 && (
          <p className="muted">
            {ideas.length === 0
              ? 'No ideas yet. Create one here, or capture one in the app, and it will appear here.'
              : `Nothing matches “${search}”.`}
          </p>
        )}

        <ul className="ideas">
          {shown.map((idea) => (
            <li key={idea.id}>
              {/* The title is the way in to editing. Kept in --text rather than --accent so
                  the card hierarchy survives; the hover state is what signals the link. */}
              <h2>
                <Link to={`/idea/${idea.id}`}>{idea.title || 'Untitled'}</Link>
              </h2>
              {idea.description && <p>{idea.description}</p>}
              {idea.images && idea.images.length > 0 && (
                <div className="thumbs">
                  {idea.images.map((src, i, all) => (
                    /* The wrapper is the 96px box and the image fills it — the same shape
                       the editor uses. An <img> carrying .thumb itself cannot work: the
                       `.thumb img` rule that makes the picture fill its box also matches
                       it, and width/height 100% then overrides the box's own size. */
                    <div className="thumb" key={src}>
                      <button
                        type="button"
                        className="thumb-open"
                        aria-label={`View image ${i + 1} of ${all.length}`}
                        onClick={() => setViewing({ images: all, start: i })}
                      >
                        {/* alt="" — these are the note's own photos, already described by
                            its title and description; a URL read aloud would be noise. */}
                        <img src={src} alt="" loading="lazy" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="meta">
                {idea.tags?.map((tag) => (
                  <span className="tag" key={tag}>{tag}</span>
                ))}
                {idea.createdAt && <time>{fmt.format(idea.createdAt.toDate())}</time>}
              </div>
              {idea.links && idea.links.length > 0 && (
                <ul className="links">
                  {idea.links.map((href) => (
                    <li key={href}>
                      {/* noreferrer as well as noopener: these URLs come from transcribed
                          speech and are not vetted. */}
                      <a href={href} target="_blank" rel="noopener noreferrer">{href}</a>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </main>

      {viewing && (
        <Lightbox
          images={viewing.images}
          start={viewing.start}
          onClose={() => setViewing(null)}
        />
      )}
    </>
  );
}
